import { type CSSProperties, forwardRef } from 'react';

const spinKeyframes = `
  @keyframes bb-spinner-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SIZE_MAP = {
  xs: 24,
  sm: 40,
  md: 56,
  lg: 72,
  xl: 96,
} as const;

export type BBSpinnerSize = keyof typeof SIZE_MAP;

export interface BBSpinnerProps {
  size?: BBSpinnerSize;
  color?: string;
  trackColor?: string;
  speed?: number;
  isCentered?: boolean;
  message?: string | null;
}

export const BBSpinner = forwardRef<HTMLDivElement, BBSpinnerProps>(
  (
    {
      size = 'md',
      color = '#EF4444',
      trackColor = '#1F2937',
      speed = 1.2,
      isCentered = false,
      message = null,
    },
    ref
  ) => {
    const spinnerSize = SIZE_MAP[size] ?? SIZE_MAP.md;
    const radius = (spinnerSize - 8) / 2;
    const center = spinnerSize / 2;

    const spinnerStyle: CSSProperties = {
      display: 'inline-block',
      position: 'relative',
      width: `${spinnerSize}px`,
      height: `${spinnerSize}px`,
      animation: `bb-spinner-spin ${speed}s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
    };

    const spinnerContent = (
      <>
        <style>{spinKeyframes}</style>
        <div ref={isCentered ? undefined : ref} style={spinnerStyle}>
          <svg
            viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}
          >
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={trackColor}
              strokeWidth="4"
            />
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth="4"
              strokeDasharray="25 126"
              strokeDashoffset="0"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </>
    );

    if (!isCentered) {
      return spinnerContent;
    }

    const messageOffset = message ? '16px' : '0px';
    const outerStyle: CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: 'var(--chakra-colors-bg)',
      gap: '16px',
    };
    const innerStyle: CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      transform: `translateY(${messageOffset})`,
    };
    const messageStyle: CSSProperties = {
      fontSize: '16px',
      color: '#64748b',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    };

    return (
      <div ref={ref} style={outerStyle}>
        <div style={innerStyle}>
          {spinnerContent}
          {message && <div style={messageStyle}>{message}</div>}
        </div>
      </div>
    );
  }
);

BBSpinner.displayName = 'BBSpinner';
